/**
 * Deleting a user's scoped offline cache from device storage (#33948 AC2).
 *
 * Scoping the key (#33947, see offline.ts) made one user's cached responses
 * UNREADABLE to the next, but deleted nothing: crew rosters (incl. Last4SSN),
 * the profile and every other `GET-*` response stayed on disk after logout.
 * This purges them. Legacy UNSCOPED entries are out of scope here (#33993):
 * they cannot be attributed to a user.
 *
 * Some scoped entries hold unsynced local work (the Foreman job list carries
 * unsubmitted hours). A store that owns such an entry registers a retainer,
 * which decides what of it must survive; everything else is deleted.
 */
import {
  getHttpCacheIdentityScope,
  isHttpCacheIdentityScoped,
  setHttpCacheIdentityScope,
  setHttpCacheIdentityScopeFromToken,
} from './offline';

/** The slice of a key/value store the purge needs (NativeScript ApplicationSettings fits). */
export interface ScopedHttpCacheStore {
  getAllKeys(): string[];
  getString(key: string): string | undefined;
  setString(key: string, value: string): void;
  remove(key: string): void;
}

/**
 * Decides the fate of one scoped entry about to be purged: `undefined` means
 * "not mine" (the next retainer, or deletion, decides), `null` deletes it, and
 * a string is written back under the same key as what survives.
 */
export type ScopedHttpCacheRetainer = (
  cacheKey: string,
  rawValue: string
) => string | null | undefined;

const retainers = new Set<ScopedHttpCacheRetainer>();

/** Register a retainer. Returns an unsubscribe function. */
export function onScopedHttpCachePurge(
  retainer: ScopedHttpCacheRetainer
): () => void {
  retainers.add(retainer);
  return () => retainers.delete(retainer);
}

// `<scope>|<METHOD>-<url>`: the shape httpCacheRequestKey writes. A legacy
// entry starts with the method, and app settings carry no `|<METHOD>-`.
const SCOPED_KEY = /^([^|]+)\|(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)-/;

/** The identity scope a cache key was written under, or null if it is not a scoped cache key. */
export function httpCacheKeyScope(cacheKey: string): string | null {
  return SCOPED_KEY.exec(cacheKey ?? '')?.[1] ?? null;
}

function fateOf(cacheKey: string, raw: string): string | null {
  for (const retainer of [...retainers]) {
    const kept = retainer(cacheKey, raw);
    if (kept !== undefined) {
      return kept;
    }
  }
  return null;
}

/**
 * Delete every scoped cache entry whose scope `shouldPurge` accepts, keeping
 * what a retainer says must survive. Never throws: a purge must not break a
 * sign-in or sign-out. An entry whose retainer throws is left untouched, since
 * losing unsynced work is worse than leaving an entry for the next purge.
 */
export function purgeScopedHttpCache(
  store: ScopedHttpCacheStore,
  shouldPurge: (scope: string) => boolean
): void {
  let keys: string[];
  try {
    keys = store.getAllKeys() ?? [];
  } catch {
    return;
  }
  for (const key of keys) {
    const scope = httpCacheKeyScope(key);
    if (scope === null || !shouldPurge(scope)) {
      continue;
    }
    try {
      const raw = store.getString(key);
      const kept = raw == null ? null : fateOf(key, raw);
      if (kept === null) {
        store.remove(key);
      } else if (kept !== raw) {
        store.setString(key, kept);
      }
    } catch {
      // Leave the entry as it is (see above).
    }
  }
}

/**
 * Sign-out: release the scope, then delete what was cached under it. The
 * release comes FIRST so the scope-changing listeners flush in-memory state
 * (the Foreman selected job) under the outgoing key before the purge reads it,
 * and so nothing written after this point can land under that scope again.
 */
export function releaseAndPurgeHttpCacheIdentityScope(
  store: ScopedHttpCacheStore
): void {
  const outgoing = isHttpCacheIdentityScoped()
    ? getHttpCacheIdentityScope()
    : null;
  setHttpCacheIdentityScope(null);
  if (outgoing) {
    purgeScopedHttpCache(store, (scope) => scope === outgoing);
  }
}

/**
 * Sign-in: delete what any OTHER identity left behind. Covers the shared
 * device whose previous user's session expired without a logout, so nothing
 * purged it. A no-op while unscoped.
 */
export function purgeOtherHttpCacheIdentityScopes(
  store: ScopedHttpCacheStore
): void {
  if (!isHttpCacheIdentityScoped()) {
    return;
  }
  const current = getHttpCacheIdentityScope();
  purgeScopedHttpCache(store, (scope) => scope !== current);
}

/**
 * Sign-in with a token: scope the cache to its user (see
 * `setHttpCacheIdentityScopeFromToken`), then purge every other identity.
 */
export function scopeHttpCacheToTokenUser(
  accessToken: string | null | undefined,
  store: ScopedHttpCacheStore
): void {
  setHttpCacheIdentityScopeFromToken(accessToken);
  purgeOtherHttpCacheIdentityScopes(store);
}
